import React from 'react';
import styled, { keyframes } from 'styled-components';
import Button from '../common/Button';
import { scrollToSection } from '../../utils/scrollTo';
import { theme } from '../../styles/GlobalStyles';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.7; transform: scale(1); }
  50%       { opacity: 1;   transform: scale(1.05); }
`;

const HeroContainer = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  background-color: ${theme.bg};
  overflow: hidden;
  padding: 8rem 0 5rem;
`;

const Grid = styled.div`
  position: absolute; inset: 0;
  background-image: radial-gradient(circle, rgba(97,93,255,0.1) 1px, transparent 1px);
  background-size: 32px 32px;
  pointer-events: none;
`;

const Vignette = styled.div`
  position: absolute; inset: 0;
  background: radial-gradient(ellipse at 50% 50%, transparent 40%, ${theme.bg} 85%);
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;
  animation: ${pulse} 6s ease-in-out infinite;

  &.b1 { width: 560px; height: 560px; top: -100px; left: -80px;  background: rgba(97,93,255,0.14); animation-delay: 0s; }
  &.b2 { width: 460px; height: 460px; bottom: -80px; right: -60px; background: rgba(61,217,235,0.1); animation-delay: 2s; }
  &.b3 { width: 300px; height: 300px; top: 45%; left: 55%; background: rgba(97,93,255,0.07); animation-delay: 4s; }
`;

const TopLine = styled.div`
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(to right, transparent, rgba(97,93,255,0.4), rgba(61,217,235,0.3), transparent);
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  gap: 3rem;
  position: relative;
  z-index: 2;

  @media (max-width: 992px) { flex-direction: column; text-align: center; }
`;

const TextContent = styled.div`
  flex: 1;
  max-width: 600px;
  animation: ${fadeUp} 0.7s ease both;
  @media (max-width: 992px) { max-width: 100%; }
`;

const dotPulse = keyframes`0%,100%{opacity:1}50%{opacity:0.4}`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 1rem;
  border-radius: 100px;
  background: rgba(97,93,255,0.1);
  border: 1px solid rgba(97,93,255,0.25);
  color: #a5a2ff;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  letter-spacing: 0.02em;
`;

const Dot = styled.span`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #615DFF;
  display: inline-block;
  animation: ${dotPulse} 2s ease infinite;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.2rem, 5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: ${theme.text};
  margin-bottom: 1.5rem;

  .gradient {
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.1rem;
  color: ${theme.textMuted};
  line-height: 1.75;
  margin-bottom: 2.5rem;
  max-width: 520px;
  @media (max-width: 992px) { margin: 0 auto 2.5rem; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  @media (max-width: 992px) { justify-content: center; }
  @media (max-width: 480px) { flex-direction: column; align-items: center; }
`;

const TrustRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 2.5rem;
  flex-wrap: wrap;
  @media (max-width: 992px) { justify-content: center; }
`;

const TrustItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: ${theme.textDim};
  .check { color: #3DD9EB; font-weight: 700; }
`;

const VisualContainer = styled.div`
  flex-shrink: 0;
  width: 380px;
  animation: ${fadeUp} 0.7s ease 0.2s both;
  @media (max-width: 1100px) { width: 320px; }
  @media (max-width: 992px)  { width: 100%; max-width: 420px; }
`;

const MockCard = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(97,93,255,0.2);
  border-radius: 20px;
  padding: 1.75rem;
  backdrop-filter: blur(20px);
  box-shadow: 0 20px 60px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.04);
`;

const MockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const MockIcon = styled.div`
  width: 40px; height: 40px;
  border-radius: 10px;
  background: ${theme.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const MockStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.7rem;
  margin-bottom: 1.2rem;
`;

const StatBox = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 12px;
  padding: 0.75rem 1rem;
`;

const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${theme.text};
`;

const StatLabel = styled.div`
  font-size: 0.68rem;
  color: ${theme.textDim};
  margin-top: 2px;
`;

const VerifyBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: rgba(61,217,235,0.08);
  border: 1px solid rgba(61,217,235,0.2);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #3DD9EB;
`;

const MockTitleText = styled.div`font-weight:600;color:${theme.text};font-size:0.88rem;`;
const MockStatus = styled.div`font-size:0.7rem;color:#3DD9EB;margin-top:1px;`;

const HeroSection = () => (
  <HeroContainer id="hero">
    <Grid /><Vignette />
    <Blob className="b1" /><Blob className="b2" /><Blob className="b3" />
    <TopLine />
    <HeroContent>
      <TextContent>
        <Badge><Dot /> Trusted Product Authentication Platform</Badge>
        <HeroTitle>
          Protect Every Product.<br />
          <span className="gradient">Empower Every Customer.</span>
        </HeroTitle>
        <HeroDescription>
          Prodseal combines secure product authentication with digital warranty activation,
          ensuring your brand stays trusted in a world full of counterfeits.
        </HeroDescription>
        <ButtonGroup>
          <Button primary href="mailto:contact@prodseal.com?subject=Demo Request">Book a Demo</Button>
          <Button secondary onClick={() => scrollToSection('howitworks')}>See How It Works</Button>
        </ButtonGroup>
        <TrustRow>
          {['No app required', 'Scan in under 2s', 'White-label ready'].map(t => (
            <TrustItem key={t}><span className="check">✓</span>{t}</TrustItem>
          ))}
        </TrustRow>
      </TextContent>
      <VisualContainer>
        <MockCard>
          <MockHeader>
            <MockIcon>🛡️</MockIcon>
            <div>
              <MockTitleText>Prodseal Dashboard</MockTitleText>
              <MockStatus>● Live</MockStatus>
            </div>
          </MockHeader>
          <MockStats>
            {[{v:'48,290',l:'Products Tagged'},{v:'12,841',l:'Scans Today'},{v:'99.2%',l:'Verified Genuine'},{v:'14',l:'Fake Alerts'}].map(({v,l})=>(
              <StatBox key={l}><StatValue>{v}</StatValue><StatLabel>{l}</StatLabel></StatBox>
            ))}
          </MockStats>
          <VerifyBadge><span>✓</span><span>Authentic Product Verified</span></VerifyBadge>
        </MockCard>
      </VisualContainer>
    </HeroContent>
  </HeroContainer>
);

export default HeroSection;
